import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { eventsApi, schoolSettingsApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  CalendarDays,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  PartyPopper,
} from 'lucide-react';

const EVENT_TYPES = [
  { value: 'holiday', label: 'Holiday' },
  { value: 'exam', label: 'Exam' },
  { value: 'ptm', label: 'PTM' },
  { value: 'sports', label: 'Sports' },
  { value: 'cultural', label: 'Cultural' },
  { value: 'other', label: 'Other' },
];

const typeColor = {
  holiday: 'bg-green-500/10 text-green-600 border-green-500/20',
  exam: 'bg-red-500/10 text-red-600 border-red-500/20',
  ptm: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  sports: 'bg-orange-500/10 text-orange-600 border-orange-500/20',
  cultural: 'bg-purple-500/10 text-purple-600 border-purple-500/20',
  other: 'bg-muted text-muted-foreground border-border',
};

const emptyForm = {
  title: '',
  description: '',
  event_date: '',
  event_type: 'other',
  is_holiday: false,
};

export default function EventsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [evRes, stRes] = await Promise.all([
        eventsApi.getAll(),
        schoolSettingsApi.get().catch(() => ({ data: {} })),
      ]);
      setEvents(evRes.data.events || []);
      setSettings(stRes.data || {});
    } catch (e) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (ev) => {
    setEditing(ev);
    setForm({
      title: ev.title || '',
      description: ev.description || '',
      event_date: ev.event_date || '',
      event_type: ev.event_type || 'other',
      is_holiday: !!ev.is_holiday,
    });
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.title || !form.event_date) {
      toast.error('Title and date are required');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await eventsApi.update(editing.id, form);
        toast.success('Event updated');
      } else {
        await eventsApi.create(form);
        toast.success('Event created');
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await eventsApi.delete(id);
      toast.success('Event deleted');
      load();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="space-y-6" data-testid="events-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold">Events & Calendar</h1>
          <p className="text-muted-foreground mt-1">
            {isAdmin
              ? 'Manage school events and the Google Calendar embed shown to everyone.'
              : "Upcoming school events."}
          </p>
        </div>
        {isAdmin && (
          <Button onClick={openCreate} data-testid="add-event-button">
            <Plus className="h-4 w-4 mr-2" />
            New Event
          </Button>
        )}
      </div>

      {/* Google Calendar Embed */}
      {settings?.google_calendar_embed_url ? (
        <Card className="border border-border overflow-hidden">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-primary" />
              School Google Calendar
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <iframe
              src={settings.google_calendar_embed_url}
              className="w-full"
              style={{ border: 0, height: 600 }}
              title="School Google Calendar"
              data-testid="google-calendar-iframe"
            />
          </CardContent>
        </Card>
      ) : (
        isAdmin && (
          <Card className="border border-dashed border-border">
            <CardContent className="p-6 text-sm text-muted-foreground">
              Google Calendar not configured yet. Go to <b>Settings</b> to paste
              your school's calendar embed URL (Google Calendar → Settings →
              Integrate Calendar → "Embed code").
            </CardContent>
          </Card>
        )
      )}

      <Card className="border border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PartyPopper className="h-5 w-5 text-primary" />
            In-app Events
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">No events scheduled.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Description</TableHead>
                    {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.map((ev) => (
                    <TableRow key={ev.id} data-testid={`event-row-${ev.id}`}>
                      <TableCell className="whitespace-nowrap">{ev.event_date}</TableCell>
                      <TableCell className="font-medium">{ev.title}</TableCell>
                      <TableCell>
                        <Badge className={typeColor[ev.event_type] || typeColor.other}>
                          {ev.event_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-sm truncate">{ev.description}</TableCell>
                      {isAdmin && (
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            className="mr-2"
                            onClick={() => openEdit(ev)}
                            data-testid={`edit-event-${ev.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDelete(ev.id)}
                            data-testid={`delete-event-${ev.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Event' : 'New Event'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Title *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Annual Sports Day"
                data-testid="event-title-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={form.event_date}
                  onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                  data-testid="event-date-input"
                />
              </div>
              <div>
                <Label>Type</Label>
                <Select
                  value={form.event_type}
                  onValueChange={(v) =>
                    setForm({ ...form, event_type: v, is_holiday: v === 'holiday' })
                  }
                >
                  <SelectTrigger data-testid="event-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EVENT_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                rows={4}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Short description..."
                data-testid="event-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-event-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
